import {
  type SelectedMakeModel,
  matchesMakeModelSelection,
} from 'components/DataTable/customFilters/AssetFilter/MakeModelNestedFilter/utils';
import { type InternalBladeDesignKey } from 'horizon/components/Images/ImageViewer/ImageViewerTabs/SchematicView/InternalBladeSlicedView';

// The x-axis and silhouette scale to the longest Blade in the set, so a wider spread would place
// damages on shorter Blades too far from the tip to read together.
export const MAX_PLOT_BLADE_LENGTH_SPREAD_M = 5;

export type PlotBladeMakeModel = {
  make?: string | null;
  model?: string | null;
  length?: number | null;
};

export type PlotBladeLengths = {
  minLength?: number;
  maxLength?: number;
  makeModelsMissingLength: PlotBladeMakeModel[];
  canPlot: boolean;
};

const hasLength = ({ length }: PlotBladeMakeModel): boolean =>
  typeof length === 'number' && Number.isFinite(length) && length > 0;

export const getPlotBladeLengths = (makeModels: PlotBladeMakeModel[]): PlotBladeLengths => {
  const lengths = makeModels
    .filter(hasLength)
    .map(({ length }) => length as number)
    .sort((a, b) => a - b);
  const makeModelsMissingLength = makeModels.filter((makeModel) => !hasLength(makeModel));

  const minLength = lengths[0];
  const maxLength = lengths[lengths.length - 1];

  return {
    minLength,
    maxLength,
    makeModelsMissingLength,
    canPlot:
      !!makeModels.length &&
      !makeModelsMissingLength.length &&
      maxLength - minLength <= MAX_PLOT_BLADE_LENGTH_SPREAD_M,
  };
};

export const getCandidateMakeModels = ({
  makes,
  models,
  pairs,
  validMakeModels,
  configuredMakeModels,
}: {
  makes?: string[];
  models?: string[];
  pairs?: SelectedMakeModel[];
  validMakeModels?: SelectedMakeModel[];
  configuredMakeModels: PlotBladeMakeModel[];
}): PlotBladeMakeModel[] => {
  if (!pairs?.length && !makes?.length && !models?.length) {
    return [];
  }

  const inferredPairs = validMakeModels?.filter(({ make, model }) =>
    matchesMakeModelSelection(make, model, { makes, models })
  );
  const configuredCombinations = configuredMakeModels.filter(({ make, model }) =>
    pairs?.length
      ? matchesMakeModelSelection(make, model ?? '', { makes, models, pairs })
      : inferredPairs
        ? inferredPairs.some((pair) => pair.make === make && pair.model === (model ?? ''))
        : matchesMakeModelSelection(make, model ?? '', { makes, models })
  );

  // Explicit pairs may omit make-only selections. When valid Make/Models are provided, the
  // inferred pairs are exhaustive; callers without either source retain the legacy flat crossing.
  const selected: PlotBladeMakeModel[] = pairs?.length
    ? [
        ...pairs,
        ...(makes ?? [])
          .filter((make) => !pairs.some((pair) => pair.make === make))
          .map((make) => ({ make })),
      ]
    : inferredPairs
      ? inferredPairs
      : makes?.length && models?.length
        ? makes.flatMap((make) => models.map((model) => ({ make, model })))
        : makes?.length
          ? makes.map((make) => ({ make }))
          : (models ?? []).map((model) => ({ model }));

  // A selection the catalog does not cover has an unknown length rather than an absent one, so it
  // blocks the plot — including a combination no Blade uses, which is indistinguishable here.
  const uncovered = selected.filter(
    ({ make, model }) =>
      !configuredCombinations.some(
        (configured) =>
          (make === undefined || configured.make === make) &&
          (model === undefined || (configured.model ?? '') === model)
      )
  );

  return [...configuredCombinations, ...uncovered];
};

export const getMakeModelLabel = ({ make, model }: PlotBladeMakeModel): string =>
  [make, model].filter(Boolean).join(' ');

export const getMakeModelListLabel = (makeModels: PlotBladeMakeModel[]): string =>
  makeModels.map(getMakeModelLabel).filter(Boolean).join(', ');

export const getUniqueMakeModels = (makeModels: PlotBladeMakeModel[]): PlotBladeMakeModel[] => {
  const seen = new Set<string>();

  return makeModels.filter(({ make, model, length }) => {
    if (make == null || model == null) {
      return true;
    }

    // Length is in the key so duplicates with conflicting lengths all survive, rather than the
    // axis depending on which one arrived first
    const key = JSON.stringify([make, model, length ?? null]);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const WEB_MODEL_MAP: Record<number, InternalBladeDesignKey> = {
  1: 'single-web',
  2: 'two-web',
  3: 'three-web',
  4: 'four-web',
};
